/**
 * Which state one section of the Agent Bus ops panel is in — as a pure
 * function, so the ORDER of the checks is provable rather than buried in a
 * ternary chain. Direct sibling of `convergence-state.ts`; same bug, same
 * shape of fix.
 *
 * WHY THIS EXISTS: `RuntimeBusOps` read three independent probes through one
 * `Promise.all` and fell back to `data?.aliases ?? []` on failure, so a failed
 * read rendered as "0 registered aliases" and "No aliases registered on this
 * runtime's bus yet." A monitoring page said the bus was empty because it
 * could not look at it. That is the exact direction `lib/probe.ts` was written
 * to prevent — and this page never imported it.
 *
 * It matters more than the usual instance of this bug because an EMPTY alias
 * directory is currently the true state on a live runtime: harn-service
 * registers nothing (the harness half of D-4 was never implemented; the bus
 * lane is fixing it as P7.0). So "empty" and "could not look" are not just
 * confusable here — they are the two readings of the same blank table, on the
 * one page an operator would use to tell them apart.
 *
 * Precedence: "have not looked yet" and "could not look" BOTH outrank any
 * count, because a count derived from an unread probe is not evidence.
 */

/* Probe labels. Declared ONCE and used both as the `unreachableNames` record
   keys in the page's fetcher and as the render-time lookup, so the two cannot
   drift — a hand-copied label would silently stop matching and quietly restore
   the "empty means healthy" bug this module exists to prevent.

   They live HERE rather than in the page so the pin that asserts them can be a
   pure unit test: importing the page pulled its whole component tree into a
   selector test and intermittently blew a 5 s timeout, which is a false RED on
   a guard whose entire value is that a red means something. */
export const PROBE_ALIASES = "alias directory";
export const PROBE_AGENTS = "agent descriptions";
export const PROBE_HISTORY = "history";
export const PROBE_JOURNAL = "event journal";
export const BUS_PROBES = [PROBE_ALIASES, PROBE_AGENTS, PROBE_HISTORY, PROBE_JOURNAL] as const;

export type BusSectionState =
  /** No fetch has settled yet. Assert nothing about this section. */
  | { kind: "loading" }
  /** This section's probe failed. NOT the same as an empty section. */
  | { kind: "unreachable"; probe: string }
  /** A SUCCESSFUL read that genuinely found nothing. */
  | { kind: "empty" }
  /** A successful read with rows to show. */
  | { kind: "rows" };

export interface BusSectionInputs {
  /** This section's probe label, as declared in the fetcher's
   *  `unreachableNames` record. Pass the shared constant, never a literal —
   *  a hand-copied label silently stops matching. */
  probe: string;
  /** Labels of the probes that FAILED in the last settled fetch, or `null`
   *  when no fetch has settled (`usePolling().data === null`). */
  unreachable: string[] | null;
  /** True when the fetch itself threw, before any probe could settle. */
  failed: boolean;
  /** Rows this section holds. Consulted ONLY once the read is known good. */
  count: number;
}

export function selectBusSectionState(i: BusSectionInputs): BusSectionState {
  // The fetch blew up outright — we have no per-probe detail, but we do know
  // we could not look. Checked before `loading` so a hard failure never sits
  // on a spinner forever.
  if (i.unreachable === null && i.failed) return { kind: "unreachable", probe: i.probe };

  // Nothing has settled yet. "Not looked yet" is not "nothing there".
  if (i.unreachable === null) return { kind: "loading" };

  // This section's own probe failed while its siblings may have answered —
  // which is the whole reason the fetcher settles them independently.
  if (i.unreachable.includes(i.probe)) return { kind: "unreachable", probe: i.probe };

  // Only now is a count evidence of anything.
  return i.count === 0 ? { kind: "empty" } : { kind: "rows" };
}


/**
 * What the alias table's DESCRIPTION cell should say.
 *
 * WHY THIS IS NOT `descriptionByAlias.get(alias) ?? "—"`: the descriptions come
 * from a DIFFERENT probe (`/bus/agents`) than the rows (`/bus/status`). When
 * that one probe fails while its sibling answers, every cell falls back to the
 * same em-dash — and a blank description is exactly what an operator is told to
 * read as a DEFECT, because registration enforces
 * `description: z.string().min(1)` (`harn-sdk/src/bus/schema.ts`) so the broker
 * cannot emit one. The instrument was therefore ambiguous about the very thing
 * it is used to measure: "I could not read the directory" and "this alias has
 * no description" rendered identically.
 *
 * Three genuinely different states. The third is worth SEEING but is not by
 * itself an alarm: `/bus/status` maps over the very same registry `/bus/agents`
 * reads (`bus-broker.ts`'s `status()` → `store.listAgents()`), so an alias in
 * one and not the other means the registry changed BETWEEN the two concurrent
 * polls — a reap or unregister landing in the gap, which for transient
 * `harness:<taskId>` aliases is routine rather than rare. It should clear on the
 * next poll; only persistence across several is a real inconsistency. Wording it
 * as a defect would have manufactured false findings during the live pass.
 */
export type AliasDescription =
  /** The directory probe did not answer. Assert nothing about this alias. */
  | { kind: "unread" }
  /** The directory ANSWERED and does not carry this alias. */
  | { kind: "absent" }
  | { kind: "text"; text: string };

export function selectAliasDescription(i: {
  /** True only when the `/bus/agents` probe actually answered this fetch. */
  directoryAnswered: boolean;
  /** The description the directory carried for this alias, if any. */
  description: string | undefined;
}): AliasDescription {
  // Checked FIRST: a failed read can never be evidence about an alias.
  if (!i.directoryAnswered) return { kind: "unread" };
  // Empty is impossible from the broker, so an empty string means the directory
  // gave us nothing for this alias — the same thing as it being missing.
  if (!i.description) return { kind: "absent" };
  return { kind: "text", text: i.description };
}
